<script setup lang="ts">
import { NavaAppSchema } from '~/schemas/dedicated-application';

const { data: page } = await useAsyncData(() => 
  queryCollection('AppPage').first()
);

useSeoMeta({
  title: page.value?.metaTitle,
  description: page.value?.metaDescription,
  ogUrl: page.value?.linkCanonical,
  ogTitle: page.value?.title,
  ogDescription: page.value?.description,
});

useHead({
  link: [
    { rel: "canonical", href: page.value?.linkCanonical },
  ],
  script: [
    {
      type: 'application/ld+json',
      textContent: JSON.stringify(NavaAppSchema)
    },
  ],
});

const defaultsStore = useDefaultsStore();
const modalsStore = useModalsStore();
const windowScroll = useWindowScroll();
const device = useDevice();

const heroContainerRef = useTemplateRef('heroContainerRef');
const mobileContainerRef = useTemplateRef('mobileContainerRef');
const heroImgsRef = useTemplateRef('heroImgsRef');
const featureSecRef = useTemplateRef('featureSecRef');
const heroContainerRefOffset = useElementBounding(heroContainerRef);
const mobileContainerRefOffset = useElementBounding(mobileContainerRef);
const mobileImageOrder = ref<TImageOrder>([0, 1, 2, 3, 4, 5]);
const isParallaxLocked = ref(false);
const topIndicate = 400;

type TImageOrder = [number, number, number, number, number, number];

const heroImgs = computed(() => {
  const imgs = page.value?.items.map(item => ({ title: item.title, path: item.img }));
  if (imgs) return [imgs[0], imgs[2], imgs[1], imgs[0], imgs[2], imgs[1]];
  return;
});

const imageOrder = computed((): TImageOrder => {
  if (!device.isDesktop.value || isParallaxLocked.value) return mobileImageOrder.value;
  
  const scrollY = windowScroll.y.value;
  const diff = heroContainerRefOffset.height.value - topIndicate;
  const third = diff / 3;

  if (scrollY < topIndicate) return [0, 1, 2, 3, 4, 5];
  else if (scrollY > third && scrollY < third * 1.7) return [1, 2 ,3 , 4, 5, 0];
  else if (scrollY > third * 1.7) return [2, 3, 4, 5, 0, 1];
  return [0, 1, 2, 3, 4, 5];
});

const boldImgIndex = computed(() => imageOrder.value[3] % 3);

const startFixingMobileContainerRef = computed(() => {
  if (!device.isDesktop.value) return;
  return windowScroll.y.value > topIndicate + defaultsStore.headerHeight
    && mobileContainerRefOffset.top.value <= defaultsStore.headerHeight
});

const endFixingMobileContainerRef = computed(() => {
  if (!device.isDesktop.value) return;
  return heroContainerRefOffset.bottom.value < mobileContainerRefOffset.height.value + defaultsStore.headerHeight;
});


function mobileNextItem() {
  mobileImageOrder.value.push(imageOrder.value.shift()!);
}

function mobilePreviousItem() {
  mobileImageOrder.value.unshift(imageOrder.value.pop()!);
}

function addNoAnimateToHeroImgsRef() {
  heroImgsRef.value?.classList.add('no-animate');
}

function handleMobileNextItem() {
  addNoAnimateToHeroImgsRef();
  mobileNextItem();
};

function handleMobilePreviousItem() {
  addNoAnimateToHeroImgsRef();
  mobilePreviousItem();
};

function handleOpenModal() {
  modalsStore.openModal('CTA');
};

function handleOnClickDownloadApp() {
  const elem = document.getElementById('platform-access');
  isParallaxLocked.value = true;
  elem?.scrollIntoView();
  setTimeout(() => { isParallaxLocked.value = false }, 1000);
};


useSwipe(heroImgsRef, { 
  onSwipeEnd(_, direction) {
    if (direction === 'left') mobilePreviousItem();
    if (direction === 'right') mobileNextItem();
  }
});
</script>

<template>
  <template v-if="page">
    <SectionsBase 
      ref="heroContainerRef" 
      class="relative bg-linear-to-b from-primary-600 to-primary-800 pt-0! rounded-b-3xl lg:rounded-b-[50px] overflow-hidden"
    >
      <div class="container flex-center flex-col gap-8 lg:gap-10 pt-25 lg:pt-26 lg:pb-600">
        <div class="relative z-1">
          <h1 class="text-secondary-600 highlight text-center lg:before:right-10">{{ page.title }}</h1>
          <span class="text-white h1"><span class="semicolon">؛</span> {{ page.subTitle }}</span>
        </div>
        <p class="text-center h4 text-white relative z-1">{{ page.description }}</p>
        <div class="flex flex-col lg:flex-row items-center gap-4 relative z-1">
          <Button
            variant="white"
            :append-icon="{ name: 'solar:round-alt-arrow-left-bold' }"
            size="Large"
            pill
            @click="handleOnClickDownloadApp"
            class="w-43"
          >
            دانلود اپلیکیشن
          </Button>
          <Button
            variant="white"
            type="Outline"
            :append-icon="{ name: 'solar:round-alt-arrow-left-bold' }"
            size="Large"
            pill
            @click="handleOpenModal"
            class="w-43 [&_.btn-slot-parent]:w-full [&_.btn-slot-parent]:justify-between!"
          >
            مشاوره رایگان
          </Button>
        </div>
        <div 
          ref="mobileContainerRef" 
          class="w-full bg-transparent flex-center lg:absolute"
          :class="[
            startFixingMobileContainerRef && !endFixingMobileContainerRef && 'lg:fixed',
            endFixingMobileContainerRef && 'lg:top-auto! lg:bottom-0!'
          ]"
          :style="{ 
            top: startFixingMobileContainerRef
              ? `${ defaultsStore.headerHeight }px` 
              : `${ topIndicate }px`
          }"
        >
          <div
            v-for="_, index in 3"
            :key="index"
            class="hero-chip" 
            :class="`mode-${ boldImgIndex }`"
          >
            <span class="text-body1-bold">{{ page.items[boldImgIndex]?.chips[index]?.text }}</span>
            <div class="w-10 h-10 rounded-full flex-center bg-secondary-600">
              <Icon :name="page.items[boldImgIndex]?.chips[index]?.icon ?? ''" size="24" />
            </div>
          </div>
          <div v-if="heroImgs" ref="heroImgsRef" class="hero-img-container">
            <NuxtImg
              v-for="img, index in heroImgs"
              :key="img?.title"
              :src="img?.path"
              :alt="img?.title"
              width="300"
              :preload="{ fetchPriority: 'high' }"
              fetchpriority="high"
              loading="eager"
              class="hero-img"
              :class="[
                `mode-${ imageOrder[index] }`,
                index === 3 && 'animate-swipe',
                featureSecRef?.isTransformMobile && index === 1
                  ? 'translate-y-[calc(100%+120px)]' 
                  : 'translate-y-0'
              ]"
              @animationend="addNoAnimateToHeroImgsRef"
            />
          </div>
          <IconsMobile 
            class="relative w-50 lg:w-70 2xl:w-85 z-5 transition-transform duration-700 pointer-events-none" 
            :class="featureSecRef?.isTransformMobile ? 'translate-y-[calc(100%+120px)]' : 'translate-y-0'"
          />
          <div class="w-full h-full absolute bg-[url('/svg/new/backgrounds/mesh-10.svg')] bg-no-repeat bg-center top-0 lg:-top-69" />
        </div>
        <DevicesMobile class="w-full relative z-10">
          <div class="w-full flex-center flex-wrap gap-3 mb-6">
            <div
              v-for="_, index in 3"
              :key="index"
              class="flex items-center gap-2 bg-secondary-500 rounded-full py-1 px-2 border 
                border-white shadow-[0px_4px_88px_-4px_#F8C07617,0px_8px_18px_-6px_#F8C07633] backdrop-blur-sm"
            >
              <span class="text-caption">{{ page.items[boldImgIndex]?.chips[index]?.text }}</span>
              <Icon :name="page.items[boldImgIndex]?.chips[index]?.icon ?? ''" size="16" />
            </div>
          </div>
          <div 
            class="w-full max-w-75 px-2 bg-white/75 rounded-full flex items-center justify-between gap-3 
              border-2 border-white mx-auto shadow-[0px_4px_88px_-4px_#F8C07617,0px_8px_18px_-6px_#F8C07633] 
              backdrop-blur-sm"
          >
            <Button 
              type="Thirdly"
              :append-icon="{ name: 'solar:round-alt-arrow-right-bold' }"
              size="Large"
              class="p-0!"
              @click="handleMobilePreviousItem"
            />
            <div class="w-full flex-center flex-col gap-1">
              <span class="text-caption block">{{ page.items[boldImgIndex]?.title }}</span>
              <div class="w-full h-1 flex items-center justify-between bg-primary-50 rounded-full">
                <div
                  v-for="item, index in page.items"
                  :key="item.title"
                  class="w-full h-full rounded-full transition-colors duration-300"
                  :class="boldImgIndex === index && 'bg-primary-600'"
                />
              </div>
            </div>
            <Button 
              type="Thirdly"
              :append-icon="{ name: 'solar:round-alt-arrow-left-bold' }"
              size="Large"
              class="p-0!"
              @click="handleMobileNextItem"
            />
          </div>
        </DevicesMobile>
      </div>
    </SectionsBase>

    <SectionsFeaturesNavaApp
      ref="featureSecRef"
      :items="page.features"
      :isParallaxLocked
    />

    <SectionsNavatelOverview
      :title="page.navatelOverview.title"
      :subTitle="page.navatelOverview.subTitle"
      :desc="page.navatelOverview.desc"
      mode="navaApp"
    />

    <SectionsPlatformAccess 
      :title="page.platformAccess.title"
      :desc="page.platformAccess.desc"
      mode="navaApp"
    />

    <SectionsWhyNavatel
      :title="page.whyNavatel.title"
      :desc="page.whyNavatel.desc"
      :items="page.whyNavatel.items"
    />

    <SectionsComments />

    <SectionsFAQ
      :title="page.FAQ.title"
      :items="page.FAQ.items"
    />

    <SectionsCTA :title="page.CTA.title" />
  </template>
</template>

<style scoped>
@reference "~/assets/css/main.css";

.hero-img-container {
  @apply
  w-48 lg:w-full h-100 lg:h-full absolute flex justify-center items-center select-none
  rounded-4xl lg:rounded-none overflow-hidden bg-white/30 lg:bg-transparent
}

.hero-img-container .hero-img {
  @apply
  absolute w-47 lg:w-65 2xl:w-79 lg:rounded-4xl transition-all duration-700 origin-bottom
}
.hero-img-container .hero-img {
  &.mode-0 {
    @apply
    -translate-x-[calc(100%+24px)] opacity-0 z-0 lg:translate-x-0 lg:rotate-180 lg:origin-bottom-right 
    transition-none!
  }
  &.mode-1 {
    @apply
    -translate-x-[calc(100%+24px)] opacity-0 z-0 lg:translate-x-0 lg:rotate-180 lg:origin-bottom-right
  }
  &.mode-2 {
    @apply
    -translate-x-[calc(100%+24px)] lg:opacity-30 z-1 lg:translate-x-0 lg:rotate-45 lg:origin-bottom-right
  }
  &.mode-3 {
    @apply
    z-3
  }
  &.mode-4 {
    @apply
    translate-x-[calc(100%+24px)] lg:opacity-30 z-1 lg:translate-x-0 lg:-rotate-45 lg:origin-bottom-left
  }
  &.mode-5 {
    @apply
    translate-x-[calc(100%+24px)] lg:opacity-0 z-0 lg:translate-x-0 lg:-rotate-180 lg:origin-bottom-left
    transition-none lg:transition-all
  }
}

.hero-chip {
  @apply
  absolute px-2 lg:px-4 py-1 lg:py-2 bg-white/60 border border-white rounded-full hidden 
  lg:flex items-center gap-3 z-6 transition-transform duration-700 select-none pointer-events-none
}
.hero-chip:nth-child(1) {
    &.mode-0 {
      @apply
      translate-x-15 -translate-y-45
      lg:translate-x-60 lg:-translate-y-45
      2xl:translate-x-62.5 2xl:-translate-y-52.5
    }
    &.mode-1 {
      @apply
      translate-x-20 -translate-y-40
      lg:translate-x-56 lg:-translate-y-48
      2xl:translate-x-62.5 2xl:-translate-y-52.5
    }
    &.mode-2 {
      @apply
      translate-x-25 -translate-y-25
      lg:translate-x-53 lg:-translate-y-50
      2xl:translate-x-57.5 2xl:-translate-y-50
    }
}
.hero-chip:nth-child(2) {
    &.mode-0 {
      @apply
      -translate-x-15 -translate-y-20
      lg:-translate-x-60 lg:-translate-y-40
      2xl:-translate-x-62.5 2xl:-translate-y-47.5
    }
    &.mode-1 {
      @apply
      -translate-x-15 -translate-y-25
      lg:translate-x-64 lg:-translate-y-16
      2xl:translate-x-67.5 2xl:-translate-y-17.5
    }
    &.mode-2 {
      @apply
      -translate-x-15 translate-y-0
      lg:translate-x-65 lg:-translate-y-15
      2xl:translate-x-70 2xl:-translate-y-17.5
    }
}
.hero-chip:nth-child(3) {
    &.mode-0 {
      @apply
      -translate-x-15 translate-y-10
      lg:-translate-x-60 lg:translate-y-24
      2xl:-translate-x-67.5 2xl:translate-y-30
    }
    &.mode-1 {
      @apply
      -translate-x-15 translate-y-20
      lg:-translate-x-55 lg:translate-y-2
      2xl:-translate-x-57.5 2xl:translate-y-0
    }
    &.mode-2 {
      @apply
      translate-x-25 translate-y-25
      lg:-translate-x-54 lg:-translate-y-10
      2xl:-translate-x-62.5 2xl:-translate-y-15.5
    }
}

.no-animate {
  @apply
  **:animate-none!
}

.animate-swipe {
  animation: touch 4s;
  @apply
  lg:animate-none!
}
@keyframes touch {
  0%{ transform: translateX(0); }
  25%{ transform: translateX(30px); }
  50%{ transform: translateX(0); }
  75%{ transform: translateX(-30px); }
  100%{ transform: translateX(0); }
}
</style>